import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  getNotificationPermission,
  requestNotificationPermission,
  scheduleNotification,
} from '../services/notificationService';
import { getDealsForSelectedArea } from '../services/apiService';
import WalkerBottomNav from '../components/WalkerBottomNav';
import Logo from '../components/Logo';
import './JourneyPage.css';

function readArea() {
  return (
    sessionStorage.getItem('walkin_area_name') ||
    sessionStorage.getItem('walkin_shop_area') ||
    'jouw omgeving'
  );
}

export default function JourneyPage() {
  const navigate = useNavigate();
  const [toast, setToast] = useState('');
  const [allDeals, setAllDeals] = useState([]);
  const areaName = readArea();

  useEffect(() => {
    let cancelled = false;

    // Same source as the feed: the backend. No local/demo fallback.
    const load = async () => {
      // Same gebied scoping as the feed.
      try {
        const deals = await getDealsForSelectedArea();
        if (!cancelled) setAllDeals(Array.isArray(deals) ? deals : []);
      } catch {
        if (!cancelled) setAllDeals([]);
      }
    };

    load();
    window.addEventListener('focus', load);
    window.addEventListener('walkin-deals-change', load);
    return () => {
      cancelled = true;
      window.removeEventListener('focus', load);
      window.removeEventListener('walkin-deals-change', load);
    };
  }, []);

  const stops = useMemo(() => {
    const now = new Date();
    // getNearbyDeals is already scoped to the walker's position; keep only
    // deals that are live right now. Coordinates come off the deal itself.
    return allDeals
      .filter((d) =>
        d.status === 'actief' &&
        d.expiresAt && new Date(d.expiresAt) > now
      )
      .map((d) => ({
        ...d,
        shopLat: d.lat ?? null,
        shopLng: d.lng ?? null,
        shopInstructions: d.instructions ?? null,
      }));
  }, [allDeals]);

  const openStopNavigation = (stop) => {
    if (!stop.shopLat || !stop.shopLng) return;
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
    if (isIOS) {
      window.location.href =
        `maps://maps.apple.com/?daddr=${stop.shopLat},${stop.shopLng}&q=${encodeURIComponent(stop.shopName)}`;
    } else {
      window.location.href =
        `https://www.google.com/maps/dir/?api=1&destination=${stop.shopLat},${stop.shopLng}`;
    }
  };

  useEffect(() => {
    if (!toast) return;
    const id = setTimeout(() => setToast(''), 2400);
    return () => clearTimeout(id);
  }, [toast]);

  const handleReminder = async (stop) => {
    let perm = getNotificationPermission();
    if (perm !== 'granted') perm = await requestNotificationPermission();
    if (perm !== 'granted') {
      setToast('Sta meldingen toe in instellingen.');
      return;
    }
    scheduleNotification(
      `Herinnering: ${stop.shopName}`,
      stop.title,
      5000
    );
    setToast(`Herinnering ingesteld voor ${stop.shopName}`);
  };

  return (
    <div className="journey">
      <header className="journey__header">
        <div className="journey__header-top">
          <Logo size={24} />
          <button className="journey__bell" aria-label="Notificaties">
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
              <path d="M13.73 21a2 2 0 0 1-3.46 0" />
            </svg>
          </button>
        </div>
      </header>

      <main className="journey__main">
        {stops.length < 2 ? (
          <div className="journey__empty">
            <h2 className="journey__empty-title">Geen winkelroute beschikbaar</h2>
            <p className="journey__empty-sub">
              Er zijn te weinig actieve deals in {areaName} voor een route.
            </p>
            <button
              className="journey__empty-btn"
              onClick={() => navigate('/feed')}
            >
              Terug naar deals →
            </button>
          </div>
        ) : (
          <>
            <div className="journey__hero">
              <p className="journey__hero-eyebrow">Aanbevolen route</p>
              <h1 className="journey__hero-title">{areaName}</h1>
              <div className="journey__hero-stats">
                <span className="journey__hero-stat">{stops.length} stops</span>
              </div>
            </div>

            <div className="journey__timeline">
              {stops.map((stop, i) => {
                const isLast = i === stops.length - 1;
                const time = stop.validUntilTime || '23:59';
                return (
                  <div key={stop.id} className="journey__stop journey__stop--active">
                    <div className="journey__stop-track">
                      <div className="journey__stop-dot" />
                      {!isLast && <div className="journey__stop-line" />}
                    </div>

                    <div className="journey__stop-cardwrap">
                      <button
                        className="journey__stop-card"
                        onClick={() => navigate(`/deal/${stop.id}`)}
                      >
                        <div className="journey__stop-body">
                          <div className="journey__stop-meta">
                            <span className="journey__stop-deal">{stop.title}</span>
                            <span className="journey__stop-time">tot {time}</span>
                          </div>
                          <div className="journey__stop-sub">
                            <span className="journey__stop-shop">{stop.shopName}</span>
                            <span className="journey__stop-dist">stop {i + 1}</span>
                          </div>
                        </div>
                        <svg className="journey__stop-arrow" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M9 18l6-6-6-6" />
                        </svg>
                      </button>
                      <button
                        className="journey__reminder"
                        onClick={(e) => { e.stopPropagation(); handleReminder(stop); }}
                      >
                        Herinnering
                      </button>
                      {stop.shopLat && stop.shopLng && (
                        <button
                          className="journey__nav-btn"
                          onClick={(e) => { e.stopPropagation(); openStopNavigation(stop); }}
                        >
                          Navigeer
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </>
        )}
      </main>

      {toast && <div className="journey__toast">{toast}</div>}

      <WalkerBottomNav />
    </div>
  );
}
